import { ref } from 'vue';
import { setTokens } from '@/utils/auth-token';
import { logoutApi } from '@/api/auth';
import { logoutPrivyIfSession } from '@/utils/privy';
import { awaitAuthTeardown, bumpAuthGeneration } from '@/utils/auth-teardown';
import {
  consumePendingLoginRedirect,
  ensurePendingLoginRedirect,
  performLocalLogout,
  initAuthSessionState,
  invalidateAuthSession,
  persistPendingLoginRedirectForOAuth,
  promptAuthLogin,
  openAuthDialog,
  consumeAuthDialogOptions,
  setOpenLoginDialog,
  setPendingLoginRedirect,
  subscribeAuthState,
  syncAuthStateValue,
  validateAuthSession,
} from '@/utils/auth-session';

const isLoggedIn = ref(initAuthSessionState());

subscribeAuthState((loggedIn) => {
  isLoggedIn.value = loggedIn;
});

export {
  setOpenLoginDialog,
  setPendingLoginRedirect,
  ensurePendingLoginRedirect,
  persistPendingLoginRedirectForOAuth,
  consumePendingLoginRedirect,
  validateAuthSession as validateSession,
  promptAuthLogin as promptLogin,
  openAuthDialog,
  consumeAuthDialogOptions,
  invalidateAuthSession as invalidateSession,
};

export function useAuth() {
  function loginDemo() {
    isLoggedIn.value = true;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('vantag-demo-logged-in', '1');
    }
  }

  async function logout() {
    // 先发起远端登出（拦截器此时仍能读到 token），并等 Privy iframe 退干净
    const remoteLogout = logoutApi().catch(() => {});
    try {
      await logoutPrivyIfSession();
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.warn('[auth] privy logout failed', err);
      }
    }

    performLocalLogout();
    isLoggedIn.value = false;

    try {
      await Promise.all([remoteLogout, awaitAuthTeardown()]);
    } catch {
      /* 本地已登出；远端 / teardown 失败忽略 */
    }
  }

  /** @deprecated 兼容旧调用 */
  function logoutDemo() {
    logout();
  }

  function markLoggedIn() {
    // 须 notifyAuthState：软登录无整页刷新，watchlist / analytics 等靠订阅灌水
    const ok = validateAuthSession();
    if (ok) {
      // 作废进行中的 logout deferred teardown，避免误清新会话余额/充值态
      bumpAuthGeneration();
    }
    isLoggedIn.value = ok;
    return ok;
  }

  function syncAuthState() {
    isLoggedIn.value = syncAuthStateValue();
    return isLoggedIn.value;
  }

  return {
    isLoggedIn,
    loginDemo,
    logout,
    logoutDemo,
    markLoggedIn,
    setTokens,
    validateSession: validateAuthSession,
    promptLogin: promptAuthLogin,
    syncAuthState,
  };
}
